/**
 * ============================================================================
 *                              crmss 版权声明
 * ============================================================================
 * 本软件系统（crmss - 快速开发通用管理后台框架）所有代码版权归 crmss 团队所有。
 * 代码中的 crmss 版权声明不允许删除和修改。
 * ============================================================================
 */
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import NProgress from 'nprogress'
import { useUserStore } from '@/stores/user'

/**
 * crmss 路由配置
 * 定义系统所有路由规则
 *
 * @author crmss Team
 * @version 1.0.0
 */

// 静态路由
const constantRoutes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/index.vue'),
    meta: { title: '登录', hidden: true }
  },
  {
    path: '/404',
    name: '404',
    component: () => import('@/views/error/404.vue'),
    meta: { title: '404', hidden: true }
  },
  {
    path: '/system/brand',
    redirect: '/brand/info'
  },
  {
    path: '/geo',
    component: () => import('@/layouts/index.vue'),
    meta: { title: 'GEO' },
    children: [
      {
        path: '',
        name: 'GEO',
        component: () => import('@/views/geo/index.vue'),
        meta: { title: 'GEO' }
      }
    ]
  }
]

// 动态路由 - 左侧目录菜单对应的路由
export const asyncRoutes: RouteRecordRaw[] = [
  {
    path: '/dashboard',
    component: () => import('@/layouts/index.vue'),
    meta: { title: '首页' },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/index.vue'),
        meta: { title: '首页' }
      }
    ]
  },
  {
    path: '/store',
    component: () => import('@/layouts/index.vue'),
    meta: { title: '门店' },
    children: [
      {
        path: '',
        name: 'Store',
        component: () => import('@/views/store/index.vue'),
        meta: { title: '门店管理' }
      }
    ]
  },
  {
    path: '/finance',
    component: () => import('@/layouts/index.vue'),
    redirect: '/finance/management',
    meta: { title: '财务' },
    children: [
      {
        path: 'management',
        name: 'FinanceManagement',
        component: () => import('@/views/finance/management/index.vue'),
        meta: { title: '财务管理' }
      },
      {
        path: 'account',
        name: 'FinanceAccount',
        component: () => import('@/views/finance/account/index.vue'),
        meta: { title: '帐户余额' }
      },
      {
        path: 'sms',
        name: 'FinanceSms',
        component: () => import('@/views/finance/sms/index.vue'),
        meta: { title: '短信余额' }
      },
      {
        path: 'contract',
        name: 'FinanceContract',
        component: () => import('@/views/finance/contract/index.vue'),
        meta: { title: '合同管理' }
      },
      {
        path: 'wechat',
        name: 'FinanceWechat',
        component: () => import('@/views/finance/wechat/index.vue'),
        meta: { title: '微信费用' }
      },
      {
        path: 'payment',
        name: 'FinancePayment',
        component: () => import('@/views/finance/payment/index.vue'),
        meta: { title: '支付配置' }
      }
    ]
  },
  {
    path: '/brand',
    component: () => import('@/layouts/index.vue'),
    redirect: '/brand/member',
    meta: { title: '品牌' },
    children: [
      {
        path: 'member',
        name: 'BrandMember',
        component: () => import('@/views/brand/member/index.vue'),
        meta: { title: '成员管理' }
      },
      {
        path: 'info',
        name: 'BrandInfo',
        component: () => import('@/views/brand/info/index.vue'),
        meta: { title: '品牌管理' }
      },
      {
        path: 'versionPricing',
        name: 'BrandVersionPricing',
        component: () => import('@/views/brand/versionPricing/index.vue'),
        meta: { title: '版本与定价' }
      },
      {
        path: 'wechat',
        name: 'BrandWechat',
        component: () => import('@/views/brand/wechat/index.vue'),
        meta: { title: '微信平台' }
      },
      {
        path: 'wechat/mini-program/upload-code',
        name: 'MiniProgramUploadCode',
        component: () => import('@/views/brand/wechat/uploadCode/index.vue'),
        meta: { title: '小程序上传代码', hidden: true, hiddenTabs: true }
      }
    ]
  },
  {
    path: '/system',
    component: () => import('@/layouts/index.vue'),
    redirect: '/system/price',
    meta: { title: '系统' },
    children: [
      {
        path: 'price',
        name: 'SystemPrice',
        component: () => import('@/views/system/price/index.vue'),
        meta: { title: '系统价格配置' }
      },
      {
        path: 'wechat',
        name: 'SystemWechat',
        component: () => import('@/views/system/wechat/index.vue'),
        meta: { title: '微信开放平台' }
      },
      {
        path: 'material',
        name: 'SystemMaterial',
        component: () => import('@/views/system/material/index.vue'),
        meta: { title: '素材管理' }
      },
      {
        path: 'map',
        name: 'SystemMap',
        component: () => import('@/views/dashboard/index.vue'),
        meta: { title: '地图设置' }
      },
      {
        path: 'login',
        name: 'SystemLogin',
        component: () => import('@/views/dashboard/index.vue'),
        meta: { title: '登录设置' }
      },
      {
        path: 'storage',
        name: 'SystemStorage',
        component: () => import('@/views/system/storage/index.vue'),
        meta: { title: '存储设置' }
      },
      {
        path: 'brand',
        name: 'SystemBrand',
        component: () => import('@/views/system/brand/index.vue'),
        meta: { title: '品牌管理' }
      },
      {
        path: 'log',
        name: 'SystemLog',
        component: () => import('@/views/system/log/index.vue'),
        meta: { title: '系统日志' }
      }
    ]
  },
  {
    path: '/base',
    component: () => import('@/layouts/index.vue'),
    redirect: '/base/menuBase',
    meta: { title: '基础' },
    children: [
      {
        path: 'menuBase',
        name: 'BaseMenuBase',
        component: () => import('@/views/system/menuBase/index.vue'),
        meta: { title: '菜单基础' }
      }
    ]
  }
]

// 合并所有路由
const allRoutes = [...constantRoutes, ...asyncRoutes]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: allRoutes,
  scrollBehavior: () => ({ left: 0, top: 0 })
})

// crmss 路由守卫
router.beforeEach(async (to, from, next) => {
  NProgress.start()

  const userStore = useUserStore()
  const token = userStore.token

  if (token) {
    if (to.path === '/login') {
      next('/brand/member')
    } else if (to.path === '/') {
      next('/brand/member')
    } else {
      next()
    }
  } else {
    if (to.path === '/login') {
      next()
    } else {
      next(`/login?redirect=${to.path}`)
    }
  }
})

router.afterEach(() => {
  NProgress.done()
})

export default router
